import { deepCompare, isFunction } from '@agilepoint/utils-common';
import styled, { css } from 'styled-components';
import { useRef, useState, useLayoutEffect, useMemo, useEffect } from 'react';
import { ISliderProps, ISliderValue } from './slider-types';
import { SlideArea, ValueLabel } from './parts';
import { findDirection, KnobUtility } from './utils';
import { StackPanel } from '../stack-panel';
import { Label } from '../label';
import { clamp } from '../../utils';

const MinMaxLabel = styled(Label)`
    padding: 4px 6px;
    background-color: ${({ theme: { slider: { colors: { default: { slideArea } } } } }) => slideArea.emptyAreaBackground};
`;

const SliderBase = (props: ISliderProps) => {
    const {
        className, hidden, title, orientation = 'horizontal', range, max = 100, disabled, step = 10,
        showRangeValues, value, defaultValue, elementRef, editable, onChange, onRenderLabel
    } = props;

    const min = Math.min(props.min ?? 0, max - step);

    const inRangeMode = !!range;
    const initialValue = value ?? defaultValue;
    const containerRef = useRef<HTMLElement>(null);
    const [start, setStart] = useState(initialValue?.start ?? min);
    useEffect(() => {
        const startValue = initialValue?.start ?? min;
        if (startValue !== start && !inRangeMode) {
            setStart(startValue)
        }
    }, [min]);
    const [end, setEnd] = useState(initialValue?.end ?? (inRangeMode ? start + step : min));
    const isHorizontallyAligned = orientation !== 'vertical';

    useLayoutEffect(() => {
        // Reset values, if slider orientation is changed dynamically
        const start = initialValue?.start ?? min;
        const end = initialValue?.end ?? (inRangeMode ? start + step : min);
        setStart(clamp(start, min, inRangeMode ? end - step : end));
        setEnd(clamp(end, inRangeMode ? start + step : start, max));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isHorizontallyAligned, inRangeMode]);

    const { getPosition, getValue } = useMemo(() => new KnobUtility({ max, min, step }), [max, min, step]);

    useLayoutEffect(() => {
        // Set value, if rendered in controlled mode
        if (!value) {
            return;
        }
        const { start = getValue(0), end } = value;
        setStart(clamp(inRangeMode ? start : min, min, inRangeMode ? end - step : end));
        setEnd(clamp(end, inRangeMode ? start + step : start, max));
    }, [value, start, end, getValue, inRangeMode, min, step, max]);

    useLayoutEffect(() => {
        setStart(clamp(start, min, inRangeMode ? end - step : end));
        setEnd(clamp(end, inRangeMode ? start + step : start, max));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [min, max]);

    const triggerChange = (newValue: Partial<ISliderValue>) => {
        const updatedValue: ISliderValue = {
            end: newValue.end ?? end,
            start: newValue.start ?? start
        };
        if (!deepCompare({ end, start }, updatedValue)) {
            onChange?.(updatedValue);
        }
    };

    const changeEndValue = (value: number) => {
        setEnd(value);
        triggerChange({ end: value });
    };

    const changeStartValue = (value: number) => {
        setStart(value);
        triggerChange({ start: value });
    };

    const onEndChange = (endPosition: number) => {
        changeEndValue(clamp(getValue(endPosition), inRangeMode ? start + step : start, max));
    };

    const onStartChange = (startPosition: number) => {
        changeStartValue(clamp(getValue(startPosition), min, inRangeMode ? end - step : end));
    };

    const onClick = (ev: React.MouseEvent) => {
        const { current: container } = containerRef;
        if (!container || disabled) {
            return;
        }
        const { left, width, top, height } = container.getBoundingClientRect();
        const position = isHorizontallyAligned ? (ev.clientX - left) / width * 100 : ((height + top - ev.clientY) / height) * 100;
        const [closestKnob] = findDirection(position, getPosition(start), getPosition(end));
        const estimatedValue = getValue(position);
        if (!inRangeMode || closestKnob === -1) {
            setEnd(estimatedValue);
            triggerChange({ end: estimatedValue });
            return;
        }
        setStart(estimatedValue);
        triggerChange({ start: estimatedValue });
    };

    const unitSize = 100 * step / (max - min);
    const panelOrientation = isHorizontallyAligned ? 'horizontal' : 'vertical';

    return hidden ? null : (
        <div className={className} title={title} ref={elementRef}>
            <ValueLabel end={end} max={max} min={min} start={start} editable={editable} disabled={disabled}
                inRangeMode={inRangeMode} isHorizontallyAligned={isHorizontallyAligned}
                step={step} onEndChange={changeEndValue} onStartChange={changeStartValue} onRenderLabel={onRenderLabel} />
            <SlideArea isHorizontallyAligned={isHorizontallyAligned} elementRef={containerRef}
                inRangeMode={inRangeMode} disabled={disabled} unitSize={unitSize}
                start={getPosition(start)} onClick={onClick}
                end={getPosition(end, true)} onStartChange={onStartChange} onEndChange={onEndChange} />
            <StackPanel justifyContent="space-between" hidden={!showRangeValues} orientation={panelOrientation}
                flip={!isHorizontallyAligned} inline={false}>
                <MinMaxLabel>{isFunction(onRenderLabel) ? onRenderLabel(min) : min}</MinMaxLabel>
                <MinMaxLabel>{isFunction(onRenderLabel) ? onRenderLabel(max) : max}</MinMaxLabel>
            </StackPanel>
        </div>
    );
};

SliderBase.displayName = 'Slider';

export const Slider = styled(SliderBase).attrs({
    className: String('ap-slider-wrapper')
})`
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'default')};
    position: relative;
    display: inline-grid;
    ${({ orientation = 'horizontal' }) => orientation === 'horizontal'
        ? css`
            min-width: 400px;
            row-gap: ${({ theme: { slider: { composites } } }) => composites.gap};
            grid-template: auto 1fr auto / 100%;
    `
        : css`
            min-height: 400px;
            column-gap: ${({ theme: { slider: { composites } } }) => composites.gap};
            grid-template: 100% / auto 1fr auto;
    `};
`;
